import { matchPath } from 'react-router-dom';

export interface BreadcrumbRouteMetadata {
    pattern: string;
    title: string;
}

const DASHBOARD_URL: string = '/dashboard';
const CUSTOMER_DASHBOARD_URL: string = '/customer/dashboard';

export const DASHBOARD_BREADCRUMB_TITLE: string = 'Dashboard';

export const breadcrumbRouteMetadata: readonly BreadcrumbRouteMetadata[] = [
    { pattern: DASHBOARD_URL, title: DASHBOARD_BREADCRUMB_TITLE },
    { pattern: CUSTOMER_DASHBOARD_URL, title: DASHBOARD_BREADCRUMB_TITLE },
    { pattern: '/users', title: 'Users' },
    { pattern: '/users/create', title: 'Create User' },
    { pattern: '/users/:id', title: 'User Details' },
    { pattern: '/iso-users', title: 'ISO Users' },
    { pattern: '/iso-users/create', title: 'Create ISO User' },
    { pattern: '/internal-users', title: 'Internal Users' },
    { pattern: '/internal-users/create', title: 'Create Internal User' },
    { pattern: '/customer-users', title: 'Customer Users' },
    { pattern: '/customers', title: 'Customers' },
    { pattern: '/customers/:uuid', title: 'Customer Details' },
    { pattern: '/organizations/isos', title: 'ISO Organizations' },
    { pattern: '/organizations/merchants', title: 'Merchants' },
    { pattern: '/organizations/customers', title: 'Customer Organizations' },
    { pattern: '/organizations/create/iso', title: 'Create ISO' },
    { pattern: '/organizations/create/merchant', title: 'Create Merchant' },
    { pattern: '/organizations/:id', title: 'Organization Details' },
    {
        pattern: '/organizations/:id/permissions-policies',
        title: 'Permissions Policies',
    },
    {
        pattern: '/organizations/:id/permissions-policies/:policyUuid/users',
        title: 'Policy Role Assignments',
    },
    { pattern: '/organizations/:id/permissions-audit', title: 'Permissions Audit' },
    {
        pattern: '/organizations/:id/document-templates',
        title: 'Document Templates',
    },
    {
        pattern: '/organizations/:id/credential-policies/:policyUuid',
        title: 'Credential Policy',
    },
    {
        pattern:
            '/organizations/:id/credential-policies/:policyUuid/credentials/:credentialUuid/rotation-history',
        title: 'Credential Rotation History',
    },
    { pattern: '/organizations/:id/merchant-checklist', title: 'Merchant Checklist' },
    { pattern: '/merchant-backup', title: 'Merchant Backup' },
    { pattern: '/sessions', title: 'Sessions' },
    { pattern: '/single-use-tokens', title: 'Single Use Tokens' },
    { pattern: '/user-management-tokens', title: 'User Management Tokens' },
    { pattern: '/e-invoice-tokens', title: 'E-Invoice Tokens' },
    { pattern: '/downloads', title: 'Download Anchorbase' },
    { pattern: '/payment-transactions', title: 'Payment Transactions' },
    {
        pattern: '/payment-transaction-lifecycle-events',
        title: 'Payment Transaction Lifecycle Events',
    },
    { pattern: '/payment-transactions/:id', title: 'Transaction Details' },
    { pattern: '/payment-requests', title: 'Payment Requests' },
    {
        pattern: '/payment-requests/:uuid',
        title: 'Payment Request Details',
    },
    { pattern: '/card-vault', title: 'Card Vault' },
    { pattern: '/card-vault/:id', title: 'Payment Method Details' },
    { pattern: '/customer/payment-requests', title: 'Payment Requests' },
    {
        pattern: '/customer/payment-requests/:uuid',
        title: 'Payment Request Details',
    },
    { pattern: '/customer/payment-history', title: 'Recent Payments' },
    { pattern: '/customer/payment-methods', title: 'Payment Methods' },
    { pattern: '/diagnostics', title: 'Diagnostics' },
    { pattern: '/clover-webhooks', title: 'Clover Webhooks' },
    { pattern: '/encryption-keys', title: 'Encryption keys' },
    { pattern: '/encryption-keys/:secretName', title: 'Encryption key details' },
    { pattern: '/audit-log', title: 'Audit Log' },
    { pattern: '/cloud-logs', title: 'Cloud Logs' },
    { pattern: '/recent-activity', title: 'Recent Activity' },
    { pattern: '/profile', title: 'Profile' },
    { pattern: '/profile/emails', title: 'Emails' },
    { pattern: '/profile/organization', title: 'Organization' },
    { pattern: '/profile/password', title: 'Password' },
    { pattern: '/profile/security', title: 'Security' },
    {
        pattern: '/payments-infrastructure/processors',
        title: 'Processors',
    },
    {
        pattern: '/payments-infrastructure/processors/create',
        title: 'Create Processor',
    },
    {
        pattern: '/payments-infrastructure/processors/:processorName',
        title: 'Processor Details',
    },
    {
        pattern: '/payments-infrastructure/merchant-accounts',
        title: 'Merchant Accounts',
    },
    {
        pattern: '/payments-infrastructure/merchant-accounts/create',
        title: 'Create Merchant Account',
    },
    {
        pattern: '/payments-infrastructure/merchant-accounts/:merchantAccountId',
        title: 'Merchant Account Details',
    },
    {
        pattern: '/payments-infrastructure/logical-terminals',
        title: 'Logical Terminals',
    },
    {
        pattern: '/payments-infrastructure/logical-terminals/create',
        title: 'Create Logical Terminal',
    },
    {
        pattern: '/payments-infrastructure/logical-terminals/:logicalTerminalId',
        title: 'Logical Terminal Details',
    },
    {
        pattern: '/payments-infrastructure/terminal-devices',
        title: 'Terminal Devices',
    },
    {
        pattern: '/payments-infrastructure/terminal-devices/create',
        title: 'Create Terminal Device',
    },
    {
        pattern: '/payments-infrastructure/terminal-devices/:terminalDeviceId',
        title: 'Terminal Device Details',
    },
    { pattern: '/allowed-website-templates', title: 'Allowed Website Templates' },
    {
        pattern: '/allowed-website-templates/create',
        title: 'Create Allowed Website Template',
    },
    {
        pattern: '/allowed-website-templates/:templateUuid',
        title: 'Allowed Website Template Details',
    },
    {
        pattern: '/permission-policy-templates',
        title: 'Permission Policy Templates',
    },
    { pattern: '/legal-documents', title: 'Legal Documents' },
    { pattern: '/legal-documents/create', title: 'Create Legal Document' },
    { pattern: '/legal-documents/:id', title: 'Legal Document Details' },
    { pattern: '/legal-workflow', title: 'Legal Workflow' },
    { pattern: '/organization-agreements/sign', title: 'Sign Organization Agreements' },
] as const;

export function normalizeInAppUrl(url: string): string | null {
    const trimmedUrl: string = url.trim();
    if (trimmedUrl.length === 0) {
        return null;
    }

    const localOrigin: string = 'https://pillar.local';
    const parsedUrl: URL = new URL(trimmedUrl, localOrigin);

    if (parsedUrl.origin !== localOrigin) {
        return null;
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
}

export function getBreadcrumbTitleForUrl(url: string): string | null {
    const normalizedUrl: string | null = normalizeInAppUrl(url);
    if (normalizedUrl === null) {
        return null;
    }

    const parsedUrl: URL = new URL(normalizedUrl, 'https://pillar.local');
    const pathname: string = parsedUrl.pathname;

    for (const routeMetadata of breadcrumbRouteMetadata) {
        if (matchPath({ path: routeMetadata.pattern, end: true }, pathname) !== null) {
            return routeMetadata.title;
        }
    }

    return null;
}
